import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Navegacion, Operaciones, Yo } from '../api/operaciones';
import { intentosDelPin, type PersonaDeLaTablet } from '../api/ops/tablet';
import { ErrorApi } from '../api/problema';
import { HojaRoja } from '../acceso/InvitacionHoja';
import { Icono } from '../componentes/Icono';
import { enSoloLectura } from '../logica/soloLectura';
import { useEsMovil } from '../marco/tamano';
import { Taquilla } from '../pantallas/taquilla/Taquilla';
import { permisosDe, PermisosContexto } from '../permisos/permisos';
import { RUTA_TABLET } from '../rutas';
import { conTaquilla } from './inicioTablet';
import { datosDeLaSesion, useTaquillaSinConexion } from './taquillaSinConexion';
import { crearAlmacenTablet, type AlmacenTablet, type SesionMiembro } from './almacenTablet';
import { almacenEntrega, guardarEntrega, leerEntrega, type AlmacenEntrega } from './entregaFirma';
import { FirmaEnElCentro } from './FirmaEnElCentro';
import { T_FIRMA } from './textosFirma';
import { Puerta } from './Puerta';
import { Teclado } from './Teclado';
import { TEXTOS_TABLET } from './textos';
import './tablet.css';

/**
 * La tablet del negocio (1.13): emparejar con el código de 6 números de Equipo › Tablets (`pairDevice`), «¿Quién eres?
 * Toca tu nombre» (`listDeviceMembers`), PIN de 4 a 6 cifras (`startMemberSessionOnDevice`), PIN bloqueado (G174, G177),
 * sesión abierta y tablet revocada o sin contratar (G222). Usa su propia credencial de dispositivo, no la sesión del
 * panel. Lo que se abre con la sesión del miembro (Puerta, Sala, Taquilla) llega con cada módulo. «Firma en el centro» (9.13)
 * va en `/tablet/firmas` y la Taquilla (10.7, de Eventos) en `/tablet/taquilla`, siempre en modo tablet y con «Entra ya»
 * (G899: solo con la sesión de la tablet emparejada); mientras la tablet está entregada a una clienta para firmar, se abre siempre esa pantalla (en
 * el modo de la clienta o el PIN de vuelta), sea cual sea la dirección.
 */
export interface PropsTablet {
    /** Operaciones con la credencial que se le dé (la de la tablet o la sesión del miembro en la tablet). */
    crearOps: (obtenerToken: () => string | null) => Operaciones;
    ruta: string;
    /** La consulta de la dirección (`?evento=…&origen=tablet` de la taquilla). */
    busqueda?: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    /** Dónde guarda la tablet su credencial y la sesión del miembro (pruebas). */
    almacen?: AlmacenTablet;
    /** Minutos sin tocarla tras los que se cierra la sesión del miembro (la API también la cierra). */
    minutosSinUso?: number;
    /** Dónde se guarda la tablet entregada para firmar (9.13; pruebas). */
    almacenFirma?: AlmacenEntrega;
    /** Origen de la página, para la vuelta del proveedor de firma (por defecto, el del navegador). */
    origen?: string;
}


type Estado =
    | { tipo: 'emparejar' }
    | { tipo: 'cargando' }
    | { tipo: 'quien'; personas: PersonaDeLaTablet[] }
    | { tipo: 'pin'; persona: PersonaDeLaTablet }
    | { tipo: 'bloqueado'; persona: PersonaDeLaTablet }
    | { tipo: 'sesion'; sesion: SesionMiembro }
    | { tipo: 'bloqueada' }
    | { tipo: 'revocada' }
    | { tipo: 'no-contratada' };

const pila = (nombre: string) => nombre.trim().split(/\s+/)[0] ?? nombre;

/**
 * «21:45» en la zona horaria del negocio, que llega con «¿Quién eres?» (`listDeviceMembers.timezone`, G375): así está al
 * día aunque el negocio la cambie (G367). Sin ella todavía, la hora de la propia tablet.
 */
const hora = (iso: string, zona: string | null) => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', ...(zona === null ? {} : { timeZone: zona }) });

/** «Bloqueado hasta las…» si la API dice hasta cuándo (G337); si no, «PIN bloqueado». */
const textoBloqueo = (p: PersonaDeLaTablet, zona: string | null) => (p.pin_locked_until == null ? TEXTOS_TABLET.pinBloqueado : TEXTOS_TABLET.bloqueadoHasta(hora(p.pin_locked_until, zona)));

/** El `409` de un PIN bloqueado trae hasta cuándo (`PinLockedProblem`, G337). */
function bloqueadoHastaDe(e: ErrorApi): string | null {
    const hasta = (e.problema as Record<string, unknown>).pin_locked_until;
    return typeof hasta === 'string' ? hasta : null;
}

export function AppTablet({ crearOps, ruta, busqueda, navegar, almacen: dado, minutosSinUso = 10, almacenFirma, origen }: PropsTablet) {
    const almacen = useMemo(() => dado ?? crearAlmacenTablet(), [dado]);
    const almacenDeFirma = useMemo(() => almacenFirma ?? almacenEntrega(), [almacenFirma]);
    const opsTablet = useMemo(() => crearOps(() => almacen.credencial()?.credential ?? null), [crearOps, almacen]);
    const [zona, setZona] = useState<string | null>(null);
    const [estado, setEstado] = useState<Estado>(() => {
        const s = almacen.sesion();
        return almacen.credencial() === null ? { tipo: 'emparejar' } : s !== null ? { tipo: 'sesion', sesion: s } : { tipo: 'cargando' };
    });
    const [digitos, setDigitos] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);

    /** 401 con la credencial: revocada (se olvida); 403 `entitlement_required`: sin el bloque (G222). */
    const tratar = useCallback(
        (e: unknown): boolean => {
            if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                almacen.guardarCredencial(null);
                setEstado({ tipo: 'revocada' });
                return true;
            }
            if (e instanceof ErrorApi && e.codigo === 'entitlement_required') {
                setEstado({ tipo: 'no-contratada' });
                return true;
            }
            return false;
        },
        [almacen],
    );

    const cargarPersonas = useCallback(() => {
        opsTablet
            .listDeviceMembers()
            .then((r) => {
                setZona(r.timezone);
                setEstado({ tipo: 'quien', personas: r.data });
            })
            .catch((e: unknown) => {
                if (!tratar(e)) {
                    setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido conectar con Reservaria. Prueba de nuevo.');
                    setEstado({ tipo: 'quien', personas: [] });
                }
            });
    }, [opsTablet, tratar]);

    const quienEres = useCallback(() => {
        setEstado({ tipo: 'cargando' });
        setDigitos('');
        setError(null);
        cargarPersonas();
    }, [cargarPersonas]);

    // Al abrir ya emparejada y sin sesión de nadie: «¿Quién eres?».
    const alAbrir = useRef(estado.tipo === 'cargando');
    useEffect(() => {
        if (alAbrir.current) {
            alAbrir.current = false;
            cargarPersonas();
        }
    }, [cargarPersonas]);

    const cerrarSesion = useCallback(() => {
        almacen.guardarSesion(null);
        guardarEntrega(almacenDeFirma, null);
        quienEres();
    }, [almacen, almacenDeFirma, quienEres]);

    // Sesión del miembro: se cierra sola tras 10 minutos sin tocar la tablet.
    const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (estado.tipo !== 'sesion') {
            return;
        }
        const reiniciar = () => {
            if (temporizador.current !== null) {
                clearTimeout(temporizador.current);
            }
            temporizador.current = setTimeout(cerrarSesion, minutosSinUso * 60_000);
        };
        reiniciar();
        window.addEventListener('pointerdown', reiniciar);
        window.addEventListener('keydown', reiniciar);
        return () => {
            if (temporizador.current !== null) {
                clearTimeout(temporizador.current);
            }
            window.removeEventListener('pointerdown', reiniciar);
            window.removeEventListener('keydown', reiniciar);
        };
    }, [estado.tipo, cerrarSesion, minutosSinUso]);

    const emparejar = (codigo: string) => {
        setOcupado(true);
        setError(null);
        // Sin credencial todavía: `pairDevice` es anónima.
        crearOps(() => null)
            .pairDevice(codigo)
            .then((credencial) => {
                almacen.guardarCredencial(credencial);
                setOcupado(false);
                quienEres();
            })
            .catch((e: unknown) => {
                setOcupado(false);
                setDigitos('');
                if (!tratar(e)) {
                    setError(e instanceof ErrorApi && e.codigo !== 'code_invalid' ? (e.problema.detail ?? e.problema.title) : TEXTOS_TABLET.codigoMalo);
                }
            });
    };

    const entrarConPin = (persona: PersonaDeLaTablet, pin: string) => {
        setOcupado(true);
        setError(null);
        opsTablet
            .startMemberSessionOnDevice(persona.member_id, pin)
            .then((tokens) => {
                const sesion: SesionMiembro = { token: tokens.access_token, memberId: persona.member_id, nombre: persona.display_name, iniciales: persona.initials };
                almacen.guardarSesion(sesion);
                setOcupado(false);
                setDigitos('');
                setEstado({ tipo: 'sesion', sesion });
            })
            .catch((e: unknown) => {
                setOcupado(false);
                setDigitos('');
                // G873: el PIN incorrecto es un 401 con los intentos que quedan; no es la tablet revocada.
                const quedan = intentosDelPin(e);
                if (quedan !== undefined) {
                    setError(quedan === null ? T_FIRMA.pinMaloSinCifra : T_FIRMA.pinMalo(quedan));
                    return;
                }
                if (tratar(e)) {
                    return;
                }
                if (e instanceof ErrorApi && e.codigo === 'conflict') {
                    setEstado({ tipo: 'bloqueado', persona: { ...persona, pin_locked: true, pin_locked_until: bloqueadoHastaDe(e) } });
                    return;
                }
                setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : TEXTOS_TABLET.pinMalo);
            });
    };

    // Sin efectos dentro del actualizador de estado (StrictMode lo llama dos veces): se calcula con lo que hay ahora.
    const anadir = (max: number, alCompletar?: (valor: string) => void) => (d: string) => {
        if (ocupado || digitos.length >= max) {
            return;
        }
        setError(null);
        const nuevo = digitos + d;
        setDigitos(nuevo);
        if (nuevo.length === max && alCompletar !== undefined) {
            alCompletar(nuevo);
        }
    };
    const borrar = () => {
        setDigitos((a) => a.slice(0, -1));
    };

    const marcaSinEmparejar = { icono: 'tablet-smartphone' as const, texto: TEXTOS_TABLET.sinEmparejar };
    // Dónde ha quedado emparejada (G337); las emparejadas antes no lo guardaron.
    const credencial = almacen.credencial();
    const marcaEmparejada =
        credencial?.business_name === undefined || credencial.device_name === undefined
            ? null
            : { icono: 'tablet-smartphone' as const, texto: TEXTOS_TABLET.emparejadaCon(credencial.business_name, credencial.device_name) };

    switch (estado.tipo) {
        case 'emparejar':
            return (
                <HojaRoja ancha marca={marcaSinEmparejar}>
                    <div className="rv-tablet__dos">
                        <div className="rv-tablet__izq">
                            <h1 className="rv-acceso__titulo">{TEXTOS_TABLET.empareja}</h1>
                            <p className="rv-acceso__sub">{TEXTOS_TABLET.emparejaSub}</p>
                            <div className="rv-tablet__codigo" aria-label="Código de 6 números" aria-busy={ocupado}>
                                {Array.from({ length: 6 }, (_, i) => (
                                    <span key={i} className={`rv-tablet__caja${i === digitos.length ? ' rv-tablet__caja--sig' : ''}${error !== null ? ' rv-tablet__caja--error' : ''}`}>
                                        {digitos.charAt(i)}
                                    </span>
                                ))}
                            </div>
                            {error !== null && (
                                <p role="alert" className="rv-tablet__error">
                                    <Icono nombre="circle-alert" />
                                    {error}
                                </p>
                            )}
                        </div>
                        <Teclado onDigito={anadir(6, emparejar)} onBorrar={borrar} />
                    </div>
                </HojaRoja>
            );
        case 'cargando':
            return <HojaRoja titulo="Un momento…" />;
        case 'quien':
            return (
                <HojaRoja ancha marca={marcaEmparejada}>
                    <div className="rv-tablet__quien">
                        <h1 className="rv-acceso__titulo">{TEXTOS_TABLET.quien}</h1>
                        <p className="rv-acceso__sub">{estado.personas.length === 0 && error === null ? TEXTOS_TABLET.nadie : TEXTOS_TABLET.toca}</p>
                        {error !== null && (
                            <p role="alert" className="rv-tablet__error">
                                <Icono nombre="circle-alert" />
                                {error}
                            </p>
                        )}
                        <div className="rv-tablet__personas">
                            {estado.personas.map((p) => (
                                <button
                                    key={p.member_id}
                                    type="button"
                                    className="rv-tablet__persona"
                                    onClick={() => {
                                        setDigitos('');
                                        setError(null);
                                        setEstado(p.pin_locked ? { tipo: 'bloqueado', persona: p } : { tipo: 'pin', persona: p });
                                    }}
                                >
                                    <span className="rv-tablet__avatar">{p.initials}</span>
                                    <span className="rv-tablet__nombre">{p.display_name}</span>
                                    {p.pin_locked && (
                                        <span className="rv-tablet__bloq">
                                            <Icono nombre="lock" />
                                            {textoBloqueo(p, zona)}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </HojaRoja>
            );
        case 'pin': {
            const persona = estado.persona;
            return (
                <HojaRoja ancha volver={{ texto: TEXTOS_TABLET.noSoy(pila(persona.display_name)), accion: quienEres }}>
                    <div className="rv-tablet__dos">
                        <div className="rv-tablet__izq rv-tablet__izq--centro">
                            <span className="rv-tablet__avatar rv-tablet__avatar--grande">{persona.initials}</span>
                            <h1 className="rv-acceso__titulo">{TEXTOS_TABLET.hola(pila(persona.display_name))}</h1>
                            <p className="rv-acceso__sub">{TEXTOS_TABLET.escribePin}</p>
                            <div className={`rv-tablet__puntos${error !== null ? ' rv-tablet__puntos--error' : ''}`} aria-label={`${String(digitos.length)} cifras escritas`}>
                                {Array.from({ length: Math.max(4, digitos.length) }, (_, i) => (
                                    <span key={i} className={i < digitos.length ? 'rv-tablet__punto--lleno' : undefined} />
                                ))}
                            </div>
                            {error !== null && (
                                <p role="alert" className="rv-tablet__error">
                                    <Icono nombre="circle-alert" />
                                    {error}
                                </p>
                            )}
                        </div>
                        <Teclado
                            onDigito={anadir(6)}
                            onBorrar={borrar}
                            confirmar={{
                                etiqueta: TEXTOS_TABLET.entrar,
                                icono: 'check',
                                activo: digitos.length >= 4 && !ocupado,
                                accion: () => {
                                    entrarConPin(persona, digitos);
                                },
                            }}
                        />
                    </div>
                </HojaRoja>
            );
        }
        case 'bloqueado':
            return (
                <HojaRoja
                    volver={{ texto: TEXTOS_TABLET.otraPersona, accion: quienEres }}
                    baldosa={['lock', 'var(--rv-red)', '#fff']}
                    titulo={TEXTOS_TABLET.bloqueadoTitulo}
                    sub={TEXTOS_TABLET.bloqueadoTexto(pila(estado.persona.display_name))}
                >
                    {estado.persona.pin_locked_until != null && (
                        <p className="rv-tablet__bloq">
                            <Icono nombre="lock" />
                            {textoBloqueo(estado.persona, zona)}
                        </p>
                    )}
                </HojaRoja>
            );
        case 'sesion':
            // Firma en el centro (9.13), también siempre que la tablet esté entregada a una clienta.
            if ((ruta.startsWith(`${RUTA_TABLET}/firmas`) || leerEntrega(almacenDeFirma) !== null) && credencial !== null) {
                return (
                    <FirmaDeLaSesion
                        crearOps={crearOps}
                        opsTablet={opsTablet}
                        sesion={estado.sesion}
                        negocio={credencial.business_id}
                        nombreNegocio={credencial.business_name ?? ''}
                        deviceId={credencial.device_id}
                        zona={zona}
                        ruta={ruta}
                        navegar={navegar}
                        almacen={almacenDeFirma}
                        origen={origen ?? (typeof window === 'undefined' ? undefined : window.location.origin)}
                        onBloquear={() => {
                            almacen.guardarSesion(null);
                            guardarEntrega(almacenDeFirma, null);
                            setEstado({ tipo: 'bloqueada' });
                        }}
                        onCambiarPersona={cerrarSesion}
                        onSesion={(s) => {
                            almacen.guardarSesion(s);
                            setEstado({ tipo: 'sesion', sesion: s });
                        }}
                    />
                );
            }
            // La Puerta (6.1, 6.2) en `/tablet/puerta`; la taquilla (10.7, de Eventos) se abre en modo tablet.
            if (ruta.startsWith(`${RUTA_TABLET}/puerta`) && credencial !== null) {
                return (
                    <PuertaDeLaSesion
                        crearOps={crearOps}
                        opsTablet={opsTablet}
                        sesion={estado.sesion}
                        negocio={credencial.business_id}
                        nombreNegocio={credencial.business_name ?? ''}
                        nombreTablet={credencial.device_name ?? ''}
                        zona={zona}
                        onCambiarPersona={cerrarSesion}
                        onTaquilla={(g) => { navegar(`${RUTA_TABLET}/taquilla?evento=${encodeURIComponent(g.ref.id)}&modo=tablet&origen=tablet`); }}
                    />
                );
            }
            // La Taquilla (10.7) en `/tablet/taquilla`, con la sesión con PIN de la persona en esta tablet.
            if (ruta.startsWith(`${RUTA_TABLET}/taquilla`) && credencial !== null) {
                return (
                    <TaquillaDeLaSesion
                        crearOps={crearOps}
                        opsTablet={opsTablet}
                        sesion={estado.sesion}
                        negocio={credencial.business_id}
                        nombreNegocio={credencial.business_name ?? ''}
                        ruta={ruta}
                        busqueda={busqueda ?? new URLSearchParams()}
                        navegar={navegar}
                    />
                );
            }
            return (
                <InicioDeLaSesion crearOps={crearOps} sesion={estado.sesion} negocio={credencial?.business_id ?? null} navegar={navegar} cerrarSesion={cerrarSesion} />
            );
        case 'bloqueada':
            return (
                <HojaRoja
                    baldosa={['lock', 'var(--rv-ink)', '#fff']}
                    titulo={T_FIRMA.bloqueadaTitulo}
                    sub={T_FIRMA.bloqueadaTexto}
                    principal={{ texto: T_FIRMA.quienEres, icono: 'users', ocupado: 'Un momento…', accion: quienEres }}
                />
            );
        case 'revocada':
            return (
                <HojaRoja
                    marca={marcaSinEmparejar}
                    baldosa={['unplug', 'var(--rv-ink)', '#fff']}
                    titulo={TEXTOS_TABLET.revocada}
                    sub={TEXTOS_TABLET.revocadaSub}
                    principal={{ texto: TEXTOS_TABLET.deNuevo, icono: 'arrow-right', ocupado: 'Un momento…', accion: () => { setDigitos(''); setError(null); setEstado({ tipo: 'emparejar' }); } }}
                />
            );
        case 'no-contratada':
            return (
                <HojaRoja
                    baldosa={['tablet-smartphone', 'var(--rv-paper)', 'var(--rv-ink)']}
                    titulo={TEXTOS_TABLET.noContratada}
                    sub={TEXTOS_TABLET.noContratadaSub}
                    principal={{
                        texto: 'Probar de nuevo',
                        icono: 'refresh-cw',
                        ocupado: 'Un momento…',
                        accion: () => {
                            if (almacen.credencial() === null) {
                                setEstado({ tipo: 'emparejar' });
                            } else {
                                quienEres();
                            }
                        },
                    }}
                />
            );
    }
}

/** La Puerta con las operaciones de la sesión con PIN de la persona (y las de la tablet para la copia). */
function PuertaDeLaSesion({ crearOps, opsTablet, sesion, ...resto }: Omit<Parameters<typeof Puerta>[0], 'ops'> & { crearOps: PropsTablet['crearOps'] }) {
    const token = sesion.token;
    const ops = useMemo(() => crearOps(() => token), [crearOps, token]);
    // La cuenta y el menú quedan leídos para abrir la Taquilla también si después se va la conexión (G2).
    useEffect(() => { datosDeLaSesion(ops, resto.negocio, token).catch(() => undefined); }, [ops, resto.negocio, token]);
    return <Puerta ops={ops} opsTablet={opsTablet} crearOps={crearOps} sesion={sesion} {...resto} />;
}

/** Firma en el centro con las operaciones de la sesión con PIN de la persona. */
function FirmaDeLaSesion({ crearOps, sesion, ...resto }: Omit<Parameters<typeof FirmaEnElCentro>[0], 'ops'> & { crearOps: PropsTablet['crearOps'] }) {
    const token = sesion.token;
    const ops = useMemo(() => crearOps(() => token), [crearOps, token]);
    return <FirmaEnElCentro ops={ops} sesion={sesion} {...resto} />;
}

interface PropsInicioDeLaSesion {
    crearOps: PropsTablet['crearOps'];
    sesion: SesionMiembro;
    negocio: string | null;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    cerrarSesion: () => void;
}

/**
 * El inicio de la tablet con la sesión con PIN: Puerta, Taquilla (G936: solo con su permiso y su bloque, por el menú de la
 * persona) y Firmas.
 */
function InicioDeLaSesion({ crearOps, sesion, negocio, navegar, cerrarSesion }: PropsInicioDeLaSesion) {
    const token = sesion.token;
    const ops = useMemo(() => crearOps(() => token), [crearOps, token]);
    const [taquilla, setTaquilla] = useState(false);
    useEffect(() => {
        if (negocio === null) return;
        let vivo = true;
        ops.getMyNavigation(negocio)
            .then((n) => { if (vivo) setTaquilla(conTaquilla(n)); })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, negocio]);
    return (
        <div className="rv-tablet-sesion">
            <header className="rv-tablet-sesion__cab">
                <span className="rv-tablet__avatar">{sesion.iniciales}</span>
                <strong>{sesion.nombre}</strong>
                <button type="button" className="rv-tablet-sesion__cambiar" onClick={cerrarSesion}>
                    <Icono nombre="users" />
                    {TEXTOS_TABLET.cambiar}
                </button>
            </header>
            <main className="rv-tablet-sesion__hueco">
                <Icono nombre="layout-grid" />
                <strong>{TEXTOS_TABLET.aquiSeAbre}</strong>
                <span>{TEXTOS_TABLET.seDisenan}</span>
                <button type="button" className="rv-tablet-sesion__cambiar" onClick={() => { navegar(`${RUTA_TABLET}/puerta`); }}>
                    <Icono nombre="scan-line" />
                    Puerta
                </button>
                {taquilla && (
                    <button type="button" className="rv-tablet-sesion__cambiar" onClick={() => { navegar(`${RUTA_TABLET}/taquilla?modo=tablet`); }}>
                        <Icono nombre="banknote" />
                        Taquilla
                    </button>
                )}
                <button type="button" className="rv-tablet-sesion__cambiar" onClick={() => { navegar(`${RUTA_TABLET}/firmas`); }}>
                    <Icono nombre="signature" />
                    Firmas
                </button>
            </main>
            <p className="rv-tablet-sesion__pie">
                <Icono nombre="lock" />
                {TEXTOS_TABLET.seCierra}
            </p>
        </div>
    );
}

interface PropsTaquillaDeLaSesion {
    crearOps: PropsTablet['crearOps'];
    opsTablet: Operaciones;
    sesion: SesionMiembro;
    negocio: string;
    nombreNegocio: string;
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
}

/**
 * La Taquilla (10.7) con la sesión con PIN de la persona: su cuenta (`getMe`) y sus permisos (`getMyNavigation`) antes de
 * pintarla, siempre en modo tablet. Las salidas de la pantalla se quedan dentro de la tablet: «Volver a la puerta» va a
 * `/tablet/puerta` y las demás, al inicio de la tablet. Sin conexión vende con la copia de la puerta de esta tablet
 * (`useTaquillaSinConexion`, G2, G509), con la cuenta y el menú ya leídos en esta sesión (`datosDeLaSesion`).
 */
function TaquillaDeLaSesion({ crearOps, opsTablet, sesion, negocio, nombreNegocio, ruta, busqueda, navegar }: PropsTaquillaDeLaSesion) {
    const token = sesion.token;
    const ops = useMemo(() => crearOps(() => token), [crearOps, token]);
    const movil = useEsMovil();
    const sinConexion = useTaquillaSinConexion({ ops, opsTablet, negocio, miembro: sesion.memberId });
    const [datos, setDatos] = useState<{ yo: Yo; navegacion: Navegacion } | 'error' | null>(null);
    const [intento, setIntento] = useState(0);
    useEffect(() => {
        let vivo = true;
        datosDeLaSesion(ops, negocio, token)
            .then((d) => { if (vivo) { setDatos(d); } })
            .catch(() => { if (vivo) { setDatos('error'); } });
        return () => { vivo = false; };
    }, [ops, negocio, token, intento]);
    const permisos = useMemo(() => permisosDe(datos === null || datos === 'error' ? null : datos.navegacion), [datos]);
    const consulta = useMemo(() => {
        const q = new URLSearchParams(busqueda);
        if (q.get('modo') !== 'tablet') {
            q.set('modo', 'tablet');
        }
        return q;
    }, [busqueda]);
    const ir = useCallback(
        (destino: string, reemplazar?: boolean) => {
            if (destino === '/puerta') {
                navegar(`${RUTA_TABLET}/puerta`);
            } else if (destino.startsWith(`${ruta}?`)) {
                navegar(destino, reemplazar);
            } else {
                navegar(RUTA_TABLET);
            }
        },
        [navegar, ruta],
    );

    if (datos === null) {
        return <HojaRoja titulo="Un momento…" />;
    }
    if (datos === 'error') {
        return (
            <HojaRoja
                volver={{ texto: 'Volver', accion: () => { navegar(RUTA_TABLET); } }}
                baldosa={['circle-alert', 'var(--rv-red)', '#fff']}
                titulo="No he podido abrir la taquilla"
                sub="No he podido conectar con Reservaria. Prueba de nuevo."
                principal={{ texto: 'Probar de nuevo', icono: 'refresh-cw', ocupado: 'Un momento…', accion: () => { setDatos(null); setIntento((i) => i + 1); } }}
            />
        );
    }
    return (
        <PermisosContexto.Provider value={permisos}>
            <Taquilla
                sinConexion={sinConexion}
                ops={ops}
                negocio={negocio}
                yo={datos.yo}
                membresia={datos.yo.memberships.find((m) => m.business_id === negocio)}
                nombreNegocio={nombreNegocio}
                soloLectura={enSoloLectura(datos.navegacion.mode)}
                movil={movil}
                ruta={ruta}
                busqueda={consulta}
                navegar={ir}
                recargar={() => { setIntento((i) => i + 1); }}
                onSesionCaducada={() => { navegar(RUTA_TABLET); }}
            />
        </PermisosContexto.Provider>
    );
}
